import { useState } from 'react';
import { useProspectingAvailability } from './useProspectingAvailability';
import { Button, EmptyState, InlineError, Input, Select, Textarea } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { AttributionPrompt } from './AttributionPrompt';
import { MessageSelectionDialog } from './MessageSelectionDialog';
import { FollowUpPrompt } from './FollowUpPrompt';
import { LeadFollowUpList } from './LeadFollowUpList';
import { useAllMemberships, useSalesTasks } from './hooks';
import { useDealsForLead, dealJumpHref } from './leadDealLinks';
import { useDealActivityFor } from '../deals/dealActivityFeed';
import { useOrdersWhere } from '../deals/dealOrdersList';
import { useCgStatuses } from '../cgOrders/hooks';
import { Timeline } from '../profiles/Timeline';
import { orderHref } from '../profiles/profileLinks';
import {
  dealChangeItems,
  leadActivityItems,
  mergeTimeline,
  orderItems,
  salesTaskItems,
} from '../profiles/timelineItems';
import type { TimelineItem } from '../profiles/timelineItems';
import { OUTCOME_LABELS } from '../deals/types';
import { formatAmount } from '../deals/dealAmount';
import { choicesForLead, shouldOfferAttribution } from './leadAttribution';
import {
  ACTIVITY_KINDS,
  activityDayLabel,
  actorLabel,
  activityErrorMessage,
  canRemoveEntry,
  isFromList,
  useLeadActivity,
  useLogLeadActivity,
  useRemoveLeadActivity,
  wasRecordedLate,
} from './leadActivity';
import type { LeadActivityKind } from './leadActivity';
import type { Lead } from './types';

/**
 * A lead's history, and the one button that adds to it.
 *
 * Kyu: "is there a button or something we can click (like a counter) anytime
 * we email?" — this is that button, and it is the first thing in the tab
 * rather than the last, because logging is the frequent act and reading is
 * the occasional one.
 *
 * ── Why Log email sent is not in a form ────────────────────────────────────
 *
 * The common case is "I just sent that email" and it should cost one click.
 * Everything else — a call, a different date, a note — is behind "Log
 * something else", which opens the form. Putting the date picker in front of
 * the frequent action would mean the frequent action stops being done, and a
 * timeline nobody fills in is worse than no timeline, because the counters
 * would then be confidently wrong.
 */

// Formatting moved into leadActivity.ts, because whether a row is formatted
// in UTC or in the reader's timezone depends on which kind of time it holds —
// and that rule has to be stated once, next to the flag that decides it.

export function LeadActivityTab({
  lead,
  actorNames,
  todayIso,
}: {
  lead: Lead;
  /** profile id → display name, for the "who" on each line. */
  actorNames: Map<string, string>;
  todayIso: string;
}) {
  const leadId = lead.id;
  const { access } = useAuth();
  const profile = access.status === 'approved' ? access.profile : null;
  const activity = useLeadActivity(leadId);
  const log = useLogLeadActivity();
  const remove = useRemoveLeadActivity();
  const { message_enabled } = useProspectingAvailability();
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectingMessage, setSelectingMessage] = useState<string | null>(null);
  const [kind, setKind] = useState<LeadActivityKind>('call');
  const [body, setBody] = useState('');
  const [when, setWhen] = useState(todayIso);
  // The one entry whose removal is being confirmed, and the reason the audit
  // line will carry. Admin-only; the database refuses everybody else.
  const [removingId, setRemovingId] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  // Offered after outreach, never after a note or an inbound reply. Read from
  // ACTIVITY_KINDS rather than a second hand-written list of touch kinds, so
  // this cannot drift from what the database counts as a touch.
  const [promptFollowUp, setPromptFollowUp] = useState(false);
  // The row just written, while its campaign is still being asked about. Null
  // means nothing is pending. The kind rides along because the follow-up
  // prompt comes after this one and needs to know whether it was a touch.
  // See AttributionPrompt for why the question comes after the log.
  const [attributing, setAttributing] = useState<{ id: string; kind: LeadActivityKind } | null>(
    null,
  );

  const memberships = useAllMemberships();
  const choices = choicesForLead(memberships.data ?? [], leadId);

  const entries = activity.data ?? [];

  /*
    Drop 3 of docs/design-note-profiles-interlinked.md: this tab draws the
    shared Timeline. Kyu, 9 September: "all activities are there, most recent
    to oldest, whether that's an incoming email, an email sent, a note, a
    meeting, a task." The lead's own log is still the write surface (the Log
    button above), and its rows keep their touch counter, "Recorded later"
    and the admin Remove control. What is new is everything else this lead
    is part of: changes on its deals, and its CG orders.
  */
  const leadDeals = useDealsForLead(leadId);
  const dealIds = (leadDeals.data ?? []).map((deal) => deal.id);
  const dealChanges = useDealActivityFor(dealIds);
  const orders = useOrdersWhere('lead_id', leadId);
  const salesTasks = useSalesTasks();
  const leadTasks = (salesTasks.data ?? []).filter((task) => task.lead_id === leadId);
  const cgStatuses = useCgStatuses('all');
  const personName = (id: string | null) =>
    id === null ? 'Nobody' : (actorNames.get(id) ?? 'Someone at TAP');
  const byId = new Map(entries.map((entry) => [`lead-${entry.id}`, entry]));
  const items = mergeTimeline(
    leadActivityItems(entries, (row) => actorLabel(row, actorNames)).map((item) => {
      const entry = byId.get(item.id);
      if (entry === undefined) return item;
      const badges: NonNullable<TimelineItem['badges']> = [
        ...(isFromList(entry)
          ? [{ label: `Touch ${entry.touch_number ?? '?'}`, tone: 'brand' as const }]
          : []),
        ...(wasRecordedLate(entry) ? [{ label: 'Recorded later', tone: 'warning' as const }] : []),
      ];
      // The day in the tab's own words: a day-only row reads in UTC, an
      // instant in the reader's zone. The component must not re-guess it.
      return { ...item, when: activityDayLabel(entry), ...(badges.length === 0 ? {} : { badges }) };
    }),
    dealChangeItems(
      dealChanges.data ?? [],
      {
        // Stage and section ids are not loaded here; the deal's own drawer
        // names them. Owner and money still read as words.
        stageName: (id) => (id === null ? 'No stage' : 'another stage'),
        sectionName: (id) => (id === null ? 'No section' : 'another section'),
        personName,
        amount: (value) => (value === null ? 'unpriced' : formatAmount(value)),
        outcome: (value) =>
          value === 'open' || value === 'won' || value === 'lost' ? OUTCOME_LABELS[value] : '—',
      },
      (dealId) => {
        const deal = (leadDeals.data ?? []).find((row) => row.id === dealId);
        return deal === undefined ? undefined : { name: deal.name, href: dealJumpHref(deal) };
      },
    ),
    salesTaskItems(leadTasks, personName),
    orderItems(
      orders.data ?? [],
      (id) => (cgStatuses.data ?? []).find((status) => status.id === id)?.label ?? id,
      orderHref,
    ),
  );

  function record(input: { kind: LeadActivityKind; body: string; occurredOn?: string }) {
    setError('');
    log.mutate(
      {
        leadId,
        kind: input.kind,
        body: input.body,
        ...(input.occurredOn === undefined ? {} : { occurredOn: input.occurredOn }),
      },
      {
        onSuccess: (activityId) => {
          if (message_enabled && input.kind === 'linkedin' && activityId !== '')
            setSelectingMessage(activityId);
          setFormOpen(false);
          setBody('');
          setWhen(todayIso);
          // Ask about the campaign FIRST. Both prompts at once would be two
          // questions stacked under one button; the campaign is about the
          // email that just happened, the follow-up about the next one, so the
          // campaign is asked while the email is still the thing in mind.
          const askCampaign = activityId !== '' && shouldOfferAttribution(input.kind, choices);
          setAttributing(askCampaign ? { id: activityId, kind: input.kind } : null);
          setPromptFollowUp(
            !askCampaign &&
              ACTIVITY_KINDS.some((entry) => entry.kind === input.kind && entry.isTouch),
          );
        },
        onError: (cause) => setError(activityErrorMessage(cause.message)),
      },
    );
  }

  /** The campaign question is answered or waved off; offer the follow-up next. */
  function attributionDone(kind: LeadActivityKind) {
    setAttributing(null);
    setPromptFollowUp(ACTIVITY_KINDS.some((entry) => entry.kind === kind && entry.isTouch));
  }

  return (
    <div className="lead-activity">
      {error !== '' && <InlineError>{error}</InlineError>}

      <div className="lead-activity-actions">
        <Button
          size="sm"
          disabled={log.isPending}
          onClick={() => record({ kind: 'email_sent', body: '' })}
        >
          {log.isPending ? 'Logging…' : 'Log email sent'}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setFormOpen(!formOpen)}>
          {formOpen ? 'Cancel' : 'Log something else'}
        </Button>
      </div>

      <p className="tracker-field-note">
        Works whether or not this lead is on a list. Logging a touch moves a New lead to Contacted;
        it never moves a lead backwards.
      </p>

      {attributing === null ? null : (
        <AttributionPrompt
          activityId={attributing.id}
          leadId={leadId}
          choices={choices}
          kind={attributing.kind}
          activity={entries.find((entry) => entry.id === attributing.id)}
          onDone={() => attributionDone(attributing.kind)}
        />
      )}
      {message_enabled &&
        selectingMessage !== null &&
        entries.some((entry) => entry.id === selectingMessage) && (
          <MessageSelectionDialog
            subject={{ kind: 'lead', id: leadId }}
            activity={entries.find((entry) => entry.id === selectingMessage)!}
            detailActivity={entries.find((entry) => entry.id === selectingMessage)!}
            onClose={() => setSelectingMessage(null)}
          />
        )}

      {!promptFollowUp ? null : (
        <FollowUpPrompt
          leadId={leadId}
          todayIso={todayIso}
          onDone={() => setPromptFollowUp(false)}
        />
      )}

      {/* The follow-ups this lead already has. Drop 1b could create one and
          then never show it again anywhere a person could act on it, which
          made a reminder nobody could clear. */}
      <LeadFollowUpList leadId={leadId} todayIso={todayIso} />
      <hr className="lead-activity-rule" />

      {!formOpen ? null : (
        <div className="lead-activity-form">
          <Select
            label="What happened"
            value={kind}
            onChange={(event) => setKind(event.target.value as LeadActivityKind)}
            options={ACTIVITY_KINDS.map((entry) => ({ value: entry.kind, label: entry.label }))}
          />
          <Input
            label="When"
            type="date"
            value={when}
            max={todayIso}
            onChange={(event) => setWhen(event.target.value)}
          />
          <Textarea
            label="Notes (optional)"
            rows={2}
            value={body}
            placeholder="One line — what was said, what happens next"
            onChange={(event) => setBody(event.target.value)}
          />
          <p className="tracker-field-note">
            Set the real date if this happened earlier. The timeline records when it happened, not
            when you typed it.
          </p>
          <Button
            size="sm"
            disabled={log.isPending}
            onClick={() =>
              record({
                kind,
                body,
                // The picked day, sent as a day. Converting it to an instant
                // here is what made the database refuse today's date every
                // morning — the browser's midday is the server's afternoon.
                ...(when === '' ? {} : { occurredOn: when }),
              })
            }
          >
            {log.isPending ? 'Logging…' : 'Log it'}
          </Button>
        </div>
      )}

      {activity.isPending ? (
        <p className="tracker-field-note">Loading history…</p>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nothing logged yet"
          description="Log the first email, call or note and it appears here with the date and your name."
        />
      ) : (
        <Timeline
          items={items}
          actions={(item) => {
            const entry = byId.get(item.id);
            if (entry === undefined) return null;
            return (
              <>
                {message_enabled && entry.kind === 'linkedin' && entry.source === 'manual' && (
                  <Button size="sm" variant="ghost" onClick={() => setSelectingMessage(entry.id)}>
                    Message used…
                  </Button>
                )}
                {!canRemoveEntry(profile, entry) ? null : removingId !== entry.id ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRemovingId(entry.id);
                      setRemoveReason('');
                    }}
                  >
                    Remove entry…
                  </Button>
                ) : (
                  <div className="lead-activity-form">
                    <Input
                      label="Why is this entry being removed?"
                      value={removeReason}
                      placeholder="e.g. Logged on the wrong lead"
                      onChange={(event) => setRemoveReason(event.target.value)}
                    />
                    <p className="tracker-field-note">
                      Admin-only. The removal itself is logged with your name and this reason; the
                      lead&apos;s status does not change.
                    </p>
                    <div className="lead-activity-actions">
                      <Button
                        size="sm"
                        disabled={remove.isPending || removeReason.trim() === ''}
                        onClick={() => {
                          setError('');
                          remove.mutate(
                            { leadId, activityId: entry.id, reason: removeReason.trim() },
                            {
                              onSuccess: () => setRemovingId(''),
                              onError: (cause) => setError(activityErrorMessage(cause.message)),
                            },
                          );
                        }}
                      >
                        {remove.isPending ? 'Removing…' : 'Remove entry'}
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setRemovingId('')}>
                        Keep it
                      </Button>
                    </div>
                  </div>
                )}
              </>
            );
          }}
        />
      )}
    </div>
  );
}
